/**
 * "When is the last article that day?" hint for the Schedule panel on
 * the Article page (2026-10-12).
 *
 * Editors space website posts through the day, and used to open the
 * Content Calendar just to see when the last one was scheduled. The
 * Schedule panel now reads the same calendar data
 * (GET /content-calendar for one day, website items only) and shows,
 * under the date picker:
 *
 *   Last article for today scheduled for 4:32 PM
 *   Also that day: 8:00 AM · 11:30 AM          (when there are more)
 *
 * The day FOLLOWS the picker: today until a date is picked, then that
 * date, so scheduling for tomorrow shows tomorrow's line-up. The
 * article being (re)scheduled is left out of its own hint. Posts that
 * already went live count too — "went live at" — because spacing is
 * about the site's whole day, not just the queue.
 *
 * Pure and framework-free — node --test frontend/scripts/scheduleHint.test.mjs
 */
import { addDays, formatMinutes, isValidYmd, todayInTz, ymdToDate } from "./calendarDates.js";

/** The local day the hint is about: the picked date, else today. */
export function hintDay(scheduleAt, tz, now = new Date()) {
  const ymd = String(scheduleAt || "").slice(0, 10);
  return isValidYmd(ymd) ? ymd : todayInTz(tz, now);
}

/** Website items for the day, minus this article, earliest first. */
export function websiteSlots(items, articleId) {
  return (items || [])
    .filter((it) => it && it.channel === "website" && it.article_id !== articleId)
    .sort((a, b) => (a.minutes ?? 0) - (b.minutes ?? 0));
}

/** "today" | "tomorrow" | "on Tue, Oct 13" */
export function dayPhrase(day, today) {
  if (day === today) return "today";
  if (day === addDays(today, 1)) return "tomorrow";
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC", weekday: "short", month: "short", day: "numeric",
  }).format(ymdToDate(day));
  return `on ${label}`;
}

/**
 * { text, detail, slots } for the panel. `detail` lists the earlier
 * slots ("" when there's only one); `slots` is kept for the tooltip.
 */
export function scheduleHint(items, { day, today, articleId }) {
  const slots = websiteSlots(items, articleId);
  const when = dayPhrase(day, today);
  if (!slots.length) {
    return {
      text: when === "today" || when === "tomorrow"
        ? `No other articles scheduled for ${when} yet`
        : `No other articles scheduled ${when} yet`,
      detail: "",
      slots,
    };
  }
  const last = slots[slots.length - 1];
  const time = formatMinutes(last.minutes ?? 0);
  const forDay = when === "today" || when === "tomorrow" ? `for ${when}` : when;
  const text = last.state === "published"
    ? `Last article ${forDay} went live at ${time}`
    : `Last article ${forDay} scheduled for ${time}`;
  const earlier = slots.slice(0, -1).map((s) => formatMinutes(s.minutes ?? 0));
  return {
    text,
    detail: earlier.length ? `Also that day: ${earlier.join(" · ")}` : "",
    slots,
  };
}

/** Tooltip: one line per slot, "4:32 PM — Headline (scheduled)". */
export function slotsTooltip(slots) {
  return (slots || [])
    .map((s) => `${formatMinutes(s.minutes ?? 0)} — ${s.headline || "(untitled)"}${
      s.state === "published" ? " (live)" : " (scheduled)"
    }`)
    .join("\n");
}
